import axios, { AxiosError } from 'axios';
import { configApiInstance } from './config';
import { refreshSession } from './refreshSession';
import { endSession } from '@/services/endSession';

export type TCustomError = {
  message: string;
  code: number;
  name: string;
  payload: null;
};

export type TCustomAxiosError = {
  code: number;
  data: null;
  success: boolean;
  errors: TCustomError[];
};

export type TCustomAxiosResponse<T> = {
  code: number;
  success: boolean;
  errors: TCustomError[];
  data: T;
};

export type TCustomAxiosPaginationResponse<T> = TCustomAxiosResponse<T> & {
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    offset: number;
    totalItems: number;
  };
};

export type TPagination = {
  getFirstPage: () => void;
  getNextPage: () => void;
  getPreviousPage: () => void;
  getLastPage: () => void;
  currentPage: number;
  totalPages: number;
  totalItems: number;
};

export type TInfinitePagination = {
  totalItems: number;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
};

const api = axios.create(configApiInstance);

/**
 * A refresh that came back 4xx means there is no usable refresh token — the spec
 * documents 400 "No refresh token provided" alongside 401 "Invalid or expired
 * refresh token", so the whole class is fatal on this endpoint.
 *
 * A network error or a 5xx is an auth-service blip, not a dead session: the
 * request that triggered the refresh fails, but the user stays signed in.
 */
const isRefreshRejected = (error: unknown): boolean => {
  const status = (error as AxiosError)?.response?.status;

  return typeof status === 'number' && status >= 400 && status < 500;
};

/**
 * Endpoints reached *before* there is a session: sign-in, and the password-reset
 * surface.
 *
 * A 401 from any of these is a bad credential or a bad token — something the form
 * owns and shows — not an expired session. Refreshing on their behalf is
 * meaningless (there is nothing to refresh) and ending a session on their behalf
 * navigates a signed-out user away from the page they are trying to use, losing
 * the reset link they just clicked.
 */
const PRE_AUTH_PATHS = ['/login', '/forgot-password', '/set-password', '/verify-token'];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // A 401 here is a bad credential or a bad token, owned by the form, not an expiry.
    const isPreAuthRequest = PRE_AUTH_PATHS.some((path) => originalRequest?.url?.includes(path));
    // Opted out of session teardown: this request may refresh and replay, but a
    // failure of it must never sign the user out. Boot's secondary fetches set it,
    // so an outage in one of them degrades instead of ending a valid session.
    const mayEndSession = !originalRequest?.skipSessionTeardown;
    const status = error.response?.status;

    // One refresh-and-replay per request. The per-request _retry flag is the only
    // thing needed to stop a loop; the module-level retry budget this replaces was
    // shared by every request, so a burst of parallel 401s exhausted it and then
    // forced a sign-out on any later error of any status.
    const shouldTryRefresh = !isPreAuthRequest && !originalRequest?._retry && status === 401;

    if (shouldTryRefresh) {
      originalRequest._retry = true;

      try {
        await refreshSession();
        return api(originalRequest);
      } catch (refreshError) {
        if (mayEndSession && isRefreshRejected(refreshError)) endSession({ reason: 'expired' });

        return Promise.reject(refreshError);
      }
    }

    // The refresh succeeded but the replayed request still came back 401, so the
    // session is genuinely dead rather than merely stale.
    if (mayEndSession && !isPreAuthRequest && status === 401 && originalRequest?._retry) {
      endSession({ reason: 'expired' });
    }

    // A request that never got a response — offline, CORS rejection, DNS failure,
    // timeout, aborted request — has no `error.response`. Reading `.data` off it
    // throws a TypeError *inside the interceptor*, and that TypeError becomes the
    // rejection every caller sees instead of the app's error shape. Reject with a
    // synthesised TCustomAxiosError so `err.errors[0].message` keeps working.
    if (!error.response) {
      return Promise.reject({
        code: 0,
        data: null,
        success: false,
        errors: [
          {
            message: error.message || 'Network request failed.',
            code: 0,
            name: error.name || 'NetworkError',
            payload: null,
          },
        ],
      } satisfies TCustomAxiosError);
    }

    const apiError = error.response.data as TCustomAxiosError;
    return Promise.reject(apiError);
  },
);

export { api };
